/**
 * SettingsCogPanel — F3: Dashboard Settings Side Panel
 *
 * Opens as a right-side Sheet when the cog icon in the page header is clicked.
 *
 * Contents (per PRD §5.4):
 *   1. Widget Add/Remove + ordering — consistent Add/Remove button pattern for all widgets
 *   2. PM Dashboard Widgets section — shown when hasSecondaryPmDashboard is true
 *   3. Reset to default — restores persona baseline, removing all personalisation
 *
 * All widgets use the same Add/Remove button interaction — no toggles/switches.
 * The panel reads from and writes to DashboardWidgetPreferencesContext (F3/F4).
 * It consumes DashboardPersonaContext to know the active persona.
 */

import { useState } from 'react';
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Settings,
  ChevronUp,
  ChevronDown,
  Plus,
  X,
  RotateCcw,
  GripVertical,
  Loader2,
  Search,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useDashboardWidgetPreferences } from '@/contexts/DashboardWidgetPreferencesContext';
import { useDashboardPersona, PERSONA_OPTIONS } from '@/contexts/DashboardPersonaContext';
import {
  WIDGET_REGISTRY,
  type WidgetDefinition,
  type ShellPersona,
} from '@/contexts/DashboardWidgetPreferencesContext';

// ── Widget row — consistent Add/Remove + reorder pattern ─────────────────────

interface WidgetRowProps {
  widget: WidgetDefinition;
  isAdded: boolean;
  isFirst: boolean;
  isLast: boolean;
  onAdd: () => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  /** Optional badge shown next to the widget label (e.g. "PM") */
  badge?: React.ReactNode;
  /** Drag-and-drop props injected by useSortable — only present for added rows */
  dragProps?: {
    style: React.CSSProperties;
    attributes: Record<string, unknown>;
    listeners: Record<string, unknown> | undefined;
    isDragging: boolean;
  };
}

function WidgetRow({
  widget,
  isAdded,
  isFirst,
  isLast,
  onAdd,
  onRemove,
  onMoveUp,
  onMoveDown,
  badge,
  dragProps,
}: WidgetRowProps) {
  return (
    <div
      style={dragProps?.style}
      className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 transition-colors ${
        isAdded
          ? 'bg-background border-border'
          : 'bg-muted/20 border-dashed border-border/60'
      } ${dragProps?.isDragging ? 'opacity-50 shadow-lg ring-2 ring-primary/40 z-50' : ''}`}
      {...(dragProps?.attributes ?? {})}
    >
      {/* Drag handle — active when the row is added, decorative otherwise */}
      <button
        type="button"
        className={`flex-shrink-0 rounded p-0.5 -ml-0.5 touch-none ${
          isAdded
            ? 'cursor-grab active:cursor-grabbing text-muted-foreground/50 hover:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40'
            : 'cursor-default text-muted-foreground/20 pointer-events-none'
        }`}
        aria-label={isAdded ? `Drag to reorder ${widget.label}` : undefined}
        tabIndex={isAdded ? 0 : -1}
        {...(dragProps?.listeners ?? {})}
      >
        <GripVertical className="h-4 w-4" aria-hidden />
      </button>

      {/* Label + description */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <p className={`text-sm font-medium leading-tight ${!isAdded ? 'text-muted-foreground' : ''}`}>
            {widget.label}
          </p>
          {badge}
        </div>
        <p className="text-xs text-muted-foreground mt-0.5 leading-tight line-clamp-2">
          {widget.description}
        </p>
      </div>

      {/* Up / Down arrows — only shown when added */}
      {isAdded && (
        <div className="flex flex-col gap-0.5 flex-shrink-0">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-5 w-5"
                disabled={isFirst}
                onClick={onMoveUp}
                aria-label={`Move ${widget.label} up`}
              >
                <ChevronUp className="h-3 w-3" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="left">Move up</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-5 w-5"
                disabled={isLast}
                onClick={onMoveDown}
                aria-label={`Move ${widget.label} down`}
              >
                <ChevronDown className="h-3 w-3" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="left">Move down</TooltipContent>
          </Tooltip>
        </div>
      )}

      {/* Add / Remove button */}
      {isAdded ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 flex-shrink-0 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
              onClick={onRemove}
              aria-label={`Remove ${widget.label}`}
            >
              <X className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="left">Remove from dashboard</TooltipContent>
        </Tooltip>
      ) : (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="h-7 flex-shrink-0 gap-1 text-xs"
              onClick={onAdd}
              aria-label={`Add ${widget.label}`}
            >
              <Plus className="h-3 w-3" />
              Add
            </Button>
          </TooltipTrigger>
          <TooltipContent side="left">Add to your dashboard</TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}

// ── Sortable wrapper — used for added rows only ───────────────────────────────

function SortableWidgetRow(props: Omit<WidgetRowProps, 'dragProps'>) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: props.widget.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef}>
      <WidgetRow
        {...props}
        dragProps={{ style, attributes: attributes as unknown as Record<string, unknown>, listeners: listeners as Record<string, unknown> | undefined, isDragging }}
      />
    </div>
  );
}

// ── PM persona badge ──────────────────────────────────────────────────────────

function PmBadge() {
  return (
    <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300">
      PM
    </span>
  );
}

// ── Main panel ────────────────────────────────────────────────────────────────

export function SettingsCogPanel() {
  const { persona, hasSecondaryPmDashboard } = useDashboardPersona();

  // DB-5: Project Team users have a fixed view — no widget customisation.
  // The cog is hidden entirely for project_team persona.
  if (persona === 'project_team') return null;

  return (
    <SettingsCogPanelShell
      persona={persona as ShellPersona}
      hasSecondaryPmDashboard={hasSecondaryPmDashboard}
    />
  );
}

// ── Inner panel — only rendered for shell personas ────────────────────────────

function SettingsCogPanelShell({
  persona,
  hasSecondaryPmDashboard,
}: {
  persona: ShellPersona;
  hasSecondaryPmDashboard: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const {
    orderedVisibleWidgets,
    toggleWidgetVisibility,
    moveWidgetUp,
    moveWidgetDown,
    reorderWidgets,
    addCrossPersonaWidget,
    removeCrossPersonaWidget,
    resetToDefault,
    hasCustomisations,
    isLoadingPreferences,
    isSaving,
  } = useDashboardWidgetPreferences();

  // dnd-kit sensors — pointer (mouse/touch) + keyboard fallback
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function handleDragEnd(event: DragEndEvent) {
    setActiveDragId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = orderedVisibleWidgets.indexOf(active.id as string);
    const newIndex = orderedVisibleWidgets.indexOf(over.id as string);
    if (oldIndex === -1 || newIndex === -1) return;
    reorderWidgets(arrayMove(orderedVisibleWidgets, oldIndex, newIndex));
  }

  const personaLabel = PERSONA_OPTIONS.find(p => p.value === persona)?.label ?? persona;

  // Native widgets for this persona
  const nativeWidgets = WIDGET_REGISTRY[persona];

  // PM secondary widgets — only for Executive persona with PM dashboard access
  const pmWidgets = hasSecondaryPmDashboard ? WIDGET_REGISTRY['project_manager'] : [];

  // A PM widget is "added" if it currently appears in the exec's visible widget order.
  const isPmWidgetAdded = (widgetId: string) =>
    orderedVisibleWidgets.includes(widgetId);

  // ── "Your Widgets" section ────────────────────────────────────────────────
  // Shows ALL added widgets in user-defined order (native + any added PM widgets),
  // followed by unadded native widgets. Added PM widgets carry a PM badge so the
  // exec can reorder them freely alongside their native widgets.
  const allAddedIds = orderedVisibleWidgets; // already the full ordered list

  // Added entries: walk orderedVisibleWidgets to preserve order
  const addedRows: Array<{ widget: WidgetDefinition; isPm: boolean }> =
    orderedVisibleWidgets
      .map(id => {
        const native = nativeWidgets.find(w => w.id === id);
        if (native) return { widget: native, isPm: false };
        const pm = pmWidgets.find(w => w.id === id);
        if (pm) return { widget: pm, isPm: true };
        return null;
      })
      .filter((r): r is { widget: WidgetDefinition; isPm: boolean } => r !== null);

  // Unadded native widgets (not in orderedVisibleWidgets at all)
  const unaddedNativeRows = nativeWidgets.filter(
    w => !orderedVisibleWidgets.includes(w.id)
  );

  // Visible count = everything currently in the ordered list
  const totalAddedCount = orderedVisibleWidgets.length;
  // Available count must be de-duplicated by id: some widgets (e.g. WIP Aging
  // Overview) appear in both the native and PM registries, but can only be
  // added once — counting them twice inflates the total.
  const totalAvailableCount = new Set([
    ...nativeWidgets.map(w => w.id),
    ...pmWidgets.map(w => w.id),
  ]).size;

  // ── "PM Dashboard Widgets" section ───────────────────────────────────────
  // Shows only the PM widgets that have NOT yet been added — acts as the pool
  // to pick from. Once added they move up to "Your Widgets".
  const unaddedPmWidgets = pmWidgets.filter(w => !isPmWidgetAdded(w.id));

  // Search filtering — matches label or description, case-insensitive
  const normalised = searchQuery.trim().toLowerCase();
  const matchesSearch = (w: WidgetDefinition) =>
    !normalised ||
    w.label.toLowerCase().includes(normalised) ||
    w.description.toLowerCase().includes(normalised);

  const filteredAddedRows = addedRows.filter(r => matchesSearch(r.widget));
  const filteredUnaddedNativeRows = unaddedNativeRows.filter(matchesSearch);
  const filteredUnaddedPmWidgets = unaddedPmWidgets.filter(matchesSearch);

  // Combined "Your Widgets" rows for rendering
  const yourWidgetsRows = [...filteredAddedRows.map(r => ({ ...r, added: true })),
    ...filteredUnaddedNativeRows.map(w => ({ widget: w, isPm: false, added: false }))];

  const noResults = normalised &&
    yourWidgetsRows.length === 0 &&
    filteredUnaddedPmWidgets.length === 0;

  return (
    <>
      {/* Trigger button */}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => setOpen(true)}
            aria-label="Customise dashboard"
          >
            <Settings className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Customise dashboard</TooltipContent>
      </Tooltip>

      {/* Side panel */}
      <Sheet open={open} onOpenChange={(v) => { setOpen(v); if (!v) setSearchQuery(''); }}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-md flex flex-col p-0 gap-0"
        >
          {/* Header */}
          <SheetHeader className="px-4 pt-5 pb-3 border-b">
            <div className="flex items-center justify-between">
              <SheetTitle className="flex items-center gap-2 text-base">
                <Settings className="h-4 w-4 text-muted-foreground" />
                Customise Dashboard
                {isLoadingPreferences && (
                  <Loader2
                    className="h-3.5 w-3.5 text-muted-foreground animate-spin"
                    aria-label="Loading your preferences…"
                  />
                )}
              </SheetTitle>
              {hasCustomisations && (
                <Badge variant="secondary" className="text-xs font-normal">
                  Customised
                </Badge>
              )}
            </div>
            <SheetDescription className="text-xs">
              Showing{' '}
              <span className="font-medium text-foreground">{personaLabel}</span>{' '}
              dashboard — add, remove, or reorder your widgets.
            </SheetDescription>
            {/* Search */}
            <div className="relative mt-2">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
              <Input
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search widgets…"
                className="pl-8 h-8 text-sm"
                aria-label="Search widgets"
              />
            </div>
          </SheetHeader>

          {/* Scrollable body */}
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-5">

            {/* Global no-results state */}
            {noResults && (
              <div className="flex flex-col items-center justify-center py-12 text-center gap-2">
                <Search className="h-8 w-8 text-muted-foreground/30" />
                <p className="text-sm font-medium text-muted-foreground">No widgets found</p>
                <p className="text-xs text-muted-foreground/70">Try a different search term</p>
              </div>
            )}

            {/* ── Section 1: Your Widgets ─────────────────────────────────────────
                Shows added widgets (native + PM) in order, then unadded native widgets.
                Added PM widgets appear here with a PM badge so the exec can reorder them
                alongside their native widgets in one unified list.
            ── */}
            {yourWidgetsRows.length > 0 && (
              <section>
                <div className="flex items-center justify-between mb-2.5">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Your Widgets
                  </h3>
                  <span className="text-xs text-muted-foreground">
                    {totalAddedCount} of {totalAvailableCount} added
                  </span>
                </div>

                {/* Added rows — drag-and-drop sortable */}
                {filteredAddedRows.length > 0 && (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragStart={e => setActiveDragId(e.active.id as string)}
                    onDragEnd={handleDragEnd}
                    onDragCancel={() => setActiveDragId(null)}
                  >
                    <SortableContext
                      items={filteredAddedRows.map(r => r.widget.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="space-y-2 mb-2">
                        {filteredAddedRows.map(({ widget, isPm }) => {
                          const visibleIdx = allAddedIds.indexOf(widget.id);
                          return (
                            <SortableWidgetRow
                              key={widget.id}
                              widget={widget}
                              isAdded={true}
                              isFirst={visibleIdx === 0}
                              isLast={visibleIdx === allAddedIds.length - 1}
                              onAdd={() =>
                                isPm
                                  ? addCrossPersonaWidget(widget.id)
                                  : toggleWidgetVisibility(widget.id)
                              }
                              onRemove={() =>
                                isPm
                                  ? removeCrossPersonaWidget(widget.id)
                                  : toggleWidgetVisibility(widget.id)
                              }
                              onMoveUp={() => moveWidgetUp(widget.id)}
                              onMoveDown={() => moveWidgetDown(widget.id)}
                              badge={isPm ? <PmBadge /> : undefined}
                            />
                          );
                        })}
                      </div>
                    </SortableContext>
                  </DndContext>
                )}

                {/* Unadded native rows — not sortable, shown below */}
                {filteredUnaddedNativeRows.length > 0 && (
                  <div className="space-y-2">
                    {filteredUnaddedNativeRows.map(widget => (
                      <WidgetRow
                        key={widget.id}
                        widget={widget}
                        isAdded={false}
                        isFirst={false}
                        isLast={false}
                        onAdd={() => toggleWidgetVisibility(widget.id)}
                        onRemove={() => toggleWidgetVisibility(widget.id)}
                        onMoveUp={() => moveWidgetUp(widget.id)}
                        onMoveDown={() => moveWidgetDown(widget.id)}
                      />
                    ))}
                  </div>
                )}
              </section>
            )}

            {/* ── Section 2: PM Dashboard Widgets ─────────────────────────────────
                Pool of unadded PM widgets. Once an exec adds one it moves up to
                "Your Widgets". Section hides entirely when all PM widgets are added.
            ── */}
            {pmWidgets.length > 0 && filteredUnaddedPmWidgets.length > 0 && (
              <>
                <Separator />
                <section>
                  <div className="mb-2.5">
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      PM Dashboard Widgets
                    </h3>
                    <p className="text-xs text-muted-foreground mt-1">
                      Add widgets from your Project Manager dashboard to your Executive view.
                    </p>
                  </div>
                  <div className="space-y-2">
                    {filteredUnaddedPmWidgets.map(widget => (
                      <WidgetRow
                        key={widget.id}
                        widget={widget}
                        isAdded={false}
                        isFirst={false}
                        isLast={false}
                        onAdd={() => addCrossPersonaWidget(widget.id)}
                        onRemove={() => removeCrossPersonaWidget(widget.id)}
                        onMoveUp={() => moveWidgetUp(widget.id)}
                        onMoveDown={() => moveWidgetDown(widget.id)}
                        badge={<PmBadge />}
                      />
                    ))}
                  </div>
                </section>
              </>
            )}

            {/* When all PM widgets have been added, show a subtle completion note */}
            {pmWidgets.length > 0 && unaddedPmWidgets.length === 0 && !normalised && (
              <p className="text-xs text-muted-foreground text-center py-1">
                All PM widgets have been added to your dashboard.
              </p>
            )}
          </div>

          {/* Footer */}
          <SheetFooter className="px-4 py-3 border-t bg-muted/20 flex-col gap-2 sm:flex-col sm:items-stretch">
            {hasCustomisations ? (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 gap-1.5 text-muted-foreground"
                  onClick={resetToDefault}
                  disabled={isSaving}
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                  Reset to default
                </Button>
                <Button
                  size="sm"
                  className="flex-1"
                  onClick={() => setOpen(false)}
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving…' : 'Done'}
                </Button>
              </div>
            ) : (
              <Button
                size="sm"
                onClick={() => setOpen(false)}
                className="w-full"
              >
                Done
              </Button>
            )}
            <p className="text-[10px] text-muted-foreground text-center flex items-center justify-center gap-1">
              {isSaving ? (
                <>
                  <Loader2 className="h-2.5 w-2.5 animate-spin" />
                  Saving your preferences…
                </>
              ) : (
                'Changes apply immediately to your dashboard.'
              )}
            </p>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </>
  );
}
